import { t } from 'i18next'
import { FC, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid } from '@mui/material'

import GRCircularProgress from '../../../../../components/GRCircularProgress/GRCircularProgress'
import { GameCardContent } from '../../../../../features/game/components/GameCard/GameCard'
import { TrackedGame } from '../../../../../features/live-events/types/TrackedGame'
import { useGetTrackedGameQuery, useUpdateGameMutation } from '../../../../api/internal'
import { Currency, CurrencyBundle } from '../../../../types/Currency'
import { GameMutation } from '../../../../types/GameMutation'
import { CurrencyAdder } from '../CurrencyAdder/CurrencyAdder'
import { CurrencyDisplay } from '../CurrencyDisplay/CurrencyDisplay'

// Clean currency data to prevent null value warnings
const cleanCurrencyData = (currencies: Currency[]): Currency[] => {
  return (
    currencies?.map((currency) => ({
      ...currency,
      usd: currency.usd ?? undefined,
      value: currency.value ?? undefined,
      currencyDescription: currency.currencyDescription ?? '',
      purposeDescription: currency.purposeDescription ?? '',
      values:
        currency.values?.map((v) => ({
          usd: v.usd ?? undefined,
          value: v.value ?? undefined,
        })) ?? [],
    })) ?? []
  )
}

type EditPricingDataDialogProps = {
  open: boolean
  onClose: () => void
  trackedGameData: TrackedGame | undefined
  trackedGameId: string
  onError?: (message: string) => void
}

export const EditPricingDataDialog: FC<EditPricingDataDialogProps> = ({ open, onClose, trackedGameData, trackedGameId, onError }) => {
  const [postPricingDataChange] = useUpdateGameMutation()
  const getGameDataQuery = useGetTrackedGameQuery(trackedGameId)
  const defaultCurrencyValues: { currencies: Currency[] } = useMemo(
    () => ({ currencies: cleanCurrencyData(getGameDataQuery.data?.game.currencies || []) }),
    [getGameDataQuery.data?.game.currencies]
  )
  const form = useForm<{ currencies: Currency[] }>({
    defaultValues: defaultCurrencyValues,
  })
  const {
    control,
    getValues,
    reset,
    watch,
    formState: { isDirty, isValid },
  } = form

  const currencies = watch('currencies')

  const hasValidationErrors = () => {
    if (!currencies) return false

    return currencies.some((currency: any) => {
      // Check required name field
      if (!currency.name || currency.name.trim() === '') return true

      // Check duplicate names
      const duplicateNames = currencies.filter((c: any) => c.name.trim() === currency.name.trim())
      if (duplicateNames.length > 1) return true

      // Check currency values
      if (currency.values && currency.values.length > 0) {
        return currency.values.some((value: any) => {
          // Check if usd or value is empty, invalid number, or <= 0
          const usdInvalid = !value.usd || value.usd === '' || isNaN(Number(value.usd)) || Number(value.usd) <= 0
          const valueInvalid = !value.value || value.value === '' || isNaN(Number(value.value)) || Number(value.value) <= 0
          return usdInvalid || valueInvalid
        })
      }

      return false
    })
  }

  useEffect(() => {
    if (getGameDataQuery.data) {
      reset(defaultCurrencyValues)
    }
  }, [defaultCurrencyValues, getGameDataQuery.data, reset])

  const saveCurrencyValues = () => {
    const gameData = getGameDataQuery.data?.rawGame
    if (gameData) {
      const storeIds = gameData?.stores?.map((store) => {
        return store.id
      })
      const marketIds = gameData?.markets.map((market) => {
        return market.id
      })
      const currencyList = getValues('currencies').map((currency: any) => {
        // Keep real backend IDs, filter out temporary UUIDs (which contain hyphens)
        if (currency.id && currency.id.includes('-')) {
          const { id, ...currencyWithoutId } = currency
          return currencyWithoutId
        }
        return currency
      })
      const savedGame: GameMutation = { ...gameData, currencies: currencyList, stores: storeIds, markets: marketIds }
      postPricingDataChange(savedGame)
        .unwrap()
        .catch((error) => {
          if (error?.status === 'CONFLICT' || error?.status === 409) {
            onError?.('Cannot delete currencies, already in use in price points')
          } else {
            onError?.('An error occurred while saving currencies')
          }
        })
        .finally(() => {
          onClose()
          reset(defaultCurrencyValues)
        })
    }
  }

  const handleDialogClose = () => {
    onClose()
    reset(defaultCurrencyValues)
  }

  const handleCancel = () => {
    onClose()
    reset(defaultCurrencyValues)
  }

  const isLoading = getGameDataQuery.isLoading

  return isLoading ? (
    <GRCircularProgress />
  ) : (
    <Dialog open={open} onClose={handleDialogClose} fullWidth={true} maxWidth="md">
      <FormProvider {...form}>
        <DialogTitle>
          <Grid item>{trackedGameData?.game && <GameCardContent game={trackedGameData?.game} disableGameLink />}</Grid>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} wrap="wrap" direction="row" alignItems="center">
            <Controller
              name="currencies"
              control={control}
              render={({ field }) => {
                return (
                  <>
                    <CurrencyDisplay currencies={field.value} onChange={field.onChange} />
                    <Divider sx={{ width: '100%', mt: 2 }} />
                    <CurrencyAdder currencies={field.value as CurrencyBundle[]} onChange={field.onChange} />
                  </>
                )
              }}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
            <Grid item>
              <Button onClick={handleCancel} variant="text" size="small" color="primary">
                {t('internal-common:cancel')}
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={saveCurrencyValues} variant="contained" color="primary" disabled={!isDirty || !isValid || hasValidationErrors()}>
                {t('internal-common:save')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
